import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview EventBadge Component
 *
 * A comprehensive badge component for event cards that supports multiple types,
 * sizes, styles, animations, and accessibility features.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */
import { forwardRef, useState, useEffect } from 'react';
import { Radio, X, Users, XCircle, Star, Sparkles, MapPin, Monitor, Check, RotateCcw, } from 'lucide-react';
import { getBadgeConfig } from '@loci/types';
import { getBadgeClasses } from '../../utils/badge-styles';
/**
 * Icon mapping for badge types
 */
const ICON_MAP = {
    'live-now': Radio,
    'sold-out': X,
    'almost-full': Users,
    canceled: XCircle,
    featured: Star,
    new: Sparkles,
    'near-you': MapPin,
    online: Monitor,
    registered: Check,
    recurring: RotateCcw,
};
/**
 * Icon size mapping based on badge size
 */
const ICON_SIZE_MAP = {
    compact: 12,
    standard: 16,
    prominent: 20,
};
/**
 * EventBadge Component
 *
 * Displays contextual badges on event cards with proper styling,
 * animations, and accessibility features.
 */
export const EventBadge = forwardRef(({ type, size = 'standard', showIcon = true, showText = true, animated = true, showTooltipOnHover = true, className = '', style, onClick, 'data-testid': testId, }, ref) => {
    const [isHovered, setIsHovered] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    // Check for reduced motion preference
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);
        const handleChange = (e) => {
            setPrefersReducedMotion(e.matches);
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);
    // Get configuration from central registry
    const config = getBadgeConfig(type);
    if (!config) {
        console.warn(`Badge configuration not found for type: ${type}`);
        return null;
    }
    const IconComponent = ICON_MAP[type];
    const iconSize = ICON_SIZE_MAP[size];
    // Determine label based on size and settings
    const label = size === 'compact' ? config.copy.short : config.copy.primary;
    const shouldAnimate = animated && !prefersReducedMotion;
    // Size-specific styles
    const sizeClasses = {
        compact: 'h-5 px-1.5 text-xs gap-0.5',
        standard: 'h-6 px-2 text-xs gap-1',
        prominent: 'h-8 px-3 text-sm gap-1.5',
    };
    // Get color classes from variant mapping
    const colorClasses = getBadgeClasses(type, config.appearance.variant);
    // Base classes
    const baseClasses = [
        'inline-flex items-center justify-center font-semibold',
        'border transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white',
        sizeClasses[size],
        colorClasses,
    ];
    // Border radius based on type
    baseClasses.push(config.appearance.borderRadius);
    // Animation classes
    if (shouldAnimate) {
        if (type === 'live-now') {
            baseClasses.push('animate-pulse');
        }
        if (type === 'almost-full' && isHovered) {
            baseClasses.push('animate-bounce');
        }
    }
    // Interactive classes
    if (onClick) {
        baseClasses.push('cursor-pointer');
    }
    // Combine all classes
    const combinedClasses = [...baseClasses, className].join(' ');
    // Accessibility props from config
    const a11yProps = {
        role: onClick ? 'button' : config.a11y.role,
        'aria-label': config.a11y['aria-label'],
    };
    if (config.a11y['aria-live']) {
        a11yProps['aria-live'] = config.a11y['aria-live'];
    }
    if (onClick) {
        a11yProps.tabIndex = 0;
    }
    const handleKeyDown = (e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
        }
    };
    return (_jsxs("span", { ref: ref, className: combinedClasses, style: style, onClick: onClick, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), onKeyDown: handleKeyDown, title: showTooltipOnHover ? config.copy.tooltip : undefined, "data-testid": testId || `event-badge-${type}`, ...a11yProps, children: [showIcon && IconComponent && (_jsx(IconComponent, { size: iconSize, className: "flex-shrink-0", "aria-hidden": "true" })), showText && _jsx("span", { className: "whitespace-nowrap select-none", children: label })] }));
});
EventBadge.displayName = 'EventBadge';
